// The one window keydown listener (SI-5, SIGNED_IN_PLAN 3.6). Lanes never
// add their own keydown listeners on window or document: they register a
// search field or claim keys here.
//
//   registerSearch(inputEl, { kind: "k" | "f" })  -> unregister
//   claim(keys, handler, { whenFocusIn })         -> release
//
// Claims stack and the newest claim for a key wins; `whenFocusIn` (an
// element or a function returning one) limits a claim to focus inside it.
// A handler returns true when it handled the key. Keys are named "Escape",
// "Enter", "Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
// "mod+z" (⌘ on a Mac, Ctrl elsewhere), or a single character.
//
// Bound by the shell: ⌘K / Ctrl+K (search), ⌘F / Ctrl+F (the pane's search
// on Sounds, Packs, My audio and Favorites; a second press inside the field
// falls through to the browser's find), ⌃⌘S / Ctrl+Alt+S (the sidebar, from
// 1100 px, never in a field: there it is AltGr+S), "?" (the shortcuts
// sheet) and Esc (the top overlay, sheet or menu first). Not bound, because
// the browser owns them: ⌘1 to ⌘9, ⌘N, ⌘O and ⌘,.
import { shellState } from "./shellState.js";

const searches = [];
const claims = [];

// Vuetify gives every overlay .v-overlay--active, the non-modal ones too: a
// toast and the update banner (v-snackbar), a tooltip. Only dialogs, sheets
// and menus take the keyboard (M1).
const NON_MODAL = ["v-snackbar", "v-tooltip"];
export function modalOverlayIn(doc) {
  if (!doc || typeof doc.querySelectorAll !== "function") return false;
  for (const el of doc.querySelectorAll(".v-overlay--active")) {
    const cl = el && el.classList;
    if (!cl || !NON_MODAL.some((c) => cl.contains(c))) return true;
  }
  return false;
}

let env = {
  signedIn: () => false,
  width: () => (typeof window !== "undefined" ? window.innerWidth : 1280),
  openSearch: () => {},
  toggleSidebar: () => {},
  openShortcuts: () => {},
  // Close the shell's own top overlay (Settings, Inbox, a shell sheet);
  // true when it closed one.
  closeOverlay: () => false,
  // One of the shell's own overlays is up that isn't a Vuetify one (the
  // Settings window, an alert).
  overlayOpen: () => false,
  // A Vuetify menu, dialog or sheet is up: it handles Esc itself.
  vuetifyOverlayOpen: () => typeof document !== "undefined" && modalOverlayIn(document),
};

export function setShortcutEnv(partial) {
  env = { ...env, ...partial };
}

export const isMac = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");

// What the sidebar tooltip names: "⌃⌘S" on a Mac, "Ctrl+Alt+S" elsewhere.
export const sidebarKeyLabel = () => (isMac() ? "⌃⌘S" : "Ctrl+Alt+S");

export function isEditable(el) {
  if (!el || el.nodeType !== 1) return false;
  const tag = (el.tagName || "").toLowerCase();
  if (tag === "textarea" || tag === "select") return true;
  if (tag === "input") {
    const type = (el.getAttribute && el.getAttribute("type")) || "text";
    return !["button", "checkbox", "radio", "range", "submit", "reset", "file", "color"].includes(type);
  }
  return !!el.isContentEditable;
}

// The key's name in claims.
export function keyName(e, mac = isMac()) {
  const mod = mac ? e.metaKey : e.ctrlKey;
  const key = e.key === " " ? "Space" : e.key;
  if (mod && !e.altKey && typeof key === "string" && key.length === 1) return `mod+${key.toLowerCase()}`;
  return key;
}

// When a ToolbarSearch shows its ⌘K / ⌘F hint (the app's ClSearchField
// with ClSearchClear.whileFocused): once a keyboard is in use, while the
// field is empty, and while it is unfocused even with text in it. Only
// focused with text does it give way (the app's clear button; the web
// draws none). Never on the phone variant.
export function searchHintShows({ hint = "", text = "", focused = false, keyboardUsed = false, variant = "pane" } = {}) {
  if (!hint || !keyboardUsed || variant === "phone") return false;
  return !text || !focused;
}

export function registerSearch(inputEl, { kind = "k" } = {}) {
  if (!inputEl) return () => {};
  const entry = { el: inputEl, kind };
  searches.push(entry);
  return () => {
    const i = searches.indexOf(entry);
    if (i >= 0) searches.splice(i, 1);
  };
}

export function claim(keys, handler, { whenFocusIn = null } = {}) {
  const entry = { keys: new Set(Array.isArray(keys) ? keys : [keys]), handler, whenFocusIn };
  claims.push(entry);
  return () => {
    const i = claims.indexOf(entry);
    if (i >= 0) claims.splice(i, 1);
  };
}

const connected = (el) => !!el && (el.isConnected !== false);

function latestSearch(kind) {
  for (let i = searches.length - 1; i >= 0; i--) {
    if (searches[i].kind === kind && connected(searches[i].el)) return searches[i];
  }
  return null;
}

function within(target, scope) {
  const el = typeof scope === "function" ? scope() : scope;
  const node = el && el.$el ? el.$el : el;
  return !!node && !!target && typeof node.contains === "function" && node.contains(target);
}

function runClaims(name, e) {
  for (let i = claims.length - 1; i >= 0; i--) {
    const c = claims[i];
    if (!c.keys.has(name)) continue;
    if (c.whenFocusIn && !within(e.target, c.whenFocusIn)) continue;
    if (c.handler(e) === true) return true;
  }
  return false;
}

/** Handle one keydown. Returns true when something used it. */
export function handleKey(e, { mac = isMac() } = {}) {
  shellState.keyboardUsed = true;
  const signedIn = env.signedIn();
  const name = keyName(e, mac);
  const target = e.target;
  const inField = isEditable(target);

  // ⌘K: the pane's registered search, else Search.
  if (name === "mod+k") {
    if (!signedIn) return false;
    e.preventDefault();
    const s = latestSearch("k");
    if (s) {
      s.el.focus();
      if (typeof s.el.select === "function") s.el.select();
    } else {
      env.openSearch();
    }
    return true;
  }

  // ⌘F: the pane's search; a second press inside it is the browser's find.
  if (name === "mod+f") {
    const s = latestSearch("f");
    if (!signedIn || !s || target === s.el) return false;
    e.preventDefault();
    s.el.focus();
    if (typeof s.el.select === "function") s.el.select();
    return true;
  }

  // ⌃⌘S on a Mac, Ctrl+Alt+S elsewhere: the sidebar, from 1100 px.
  const sidebarKey = mac
    ? e.ctrlKey && e.metaKey && !e.altKey && (e.key === "s" || e.key === "S")
    : e.ctrlKey && e.altKey && !e.metaKey && (e.key === "s" || e.key === "S" || e.code === "KeyS");
  if (sidebarKey) {
    if (!signedIn || inField || env.width() < 1100) return false;
    e.preventDefault();
    env.toggleSidebar();
    return true;
  }

  if (name === "Escape") {
    if (env.vuetifyOverlayOpen()) return false;
    if (env.closeOverlay()) {
      e.preventDefault();
      return true;
    }
    if (runClaims(name, e)) {
      e.preventDefault();
      return true;
    }
    return false;
  }

  if (inField) return false;

  if (name === "?" && signedIn) {
    e.preventDefault();
    env.openShortcuts();
    return true;
  }

  // A dialog, sheet or menu is up: the pane under it keeps still (M2). Its
  // own keys are the overlay's; Esc kept its order above.
  if (env.vuetifyOverlayOpen() || env.overlayOpen()) return false;

  if (runClaims(name, e)) {
    e.preventDefault();
    return true;
  }
  return false;
}

let installed = false;
const listener = (e) => handleKey(e);

export function installShortcuts() {
  if (installed || typeof window === "undefined") return () => {};
  installed = true;
  window.addEventListener("keydown", listener);
  return () => {
    window.removeEventListener("keydown", listener);
    installed = false;
  };
}

// Tests: forget every registration.
export function resetShortcuts() {
  searches.splice(0);
  claims.splice(0);
}
